import {Injector} from "@angular/core"
import {Settings} from "@app/common/models/settings/settings"
import {AuthService} from "@app/common/services/auth/auth.service"
import {ImageProcessingService} from "@app/common/services/rendering/image-processing.service"
import {OperatorStack} from "@app/textures/texture-editor/operator-stack/operator-stack"
import {getSourceMapDataObjectIds, TextureSetRevisionData} from "@app/textures/texture-editor/texture-editor-callback"
import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {JobNodes} from "@cm/job-nodes/job-nodes"
import {displacementGenerationTask} from "@cm/job-nodes/tiling"
import {Utility} from "@cm/job-nodes/utility"
import {TextureType} from "@generated"
import {TexturesApiService} from "app/textures/service/textures-api.service"
import {descriptorByTextureType} from "app/textures/utils/texture-type-descriptor"
import {createSourceDataObjectNode} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/source-data-object-node"
import {REF_COUNT_DEBUG_ID_INITIAL} from "@cm/utils"
import {REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-system"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {generateThumbnailsDataObjectTask} from "@cm/job-nodes"

export class TextureEditProcessing {
    constructor(injector: Injector) {
        this.textureApi = injector.get(TexturesApiService)
        this.imageProcessingService = injector.get(ImageProcessingService)
        this.authService = injector.get(AuthService)
    }

    async createBakingJob(operatorStack: OperatorStack, textureSetRevision: TextureSetRevisionData, pxPerCm: number): Promise<string> {
        // abort previous job if it is still running
        await this.cancelBakingJob(textureSetRevision)
        // create new job
        const jobGraph = await this.generateJobGraph(operatorStack, textureSetRevision, pxPerCm)
        const jobId = await this.textureApi.createBakingJob(textureSetRevision, jobGraph)
        return jobId
    }

    cancelBakingJob(textureSetRevision: TextureSetRevisionData) {
        return this.textureApi.cancelBakingJob(textureSetRevision)
    }

    // TODO this is not fully working (but also not used atm)
    async createRegenerateDisplacementJob(textureSetRevision: TextureSetRevisionData, pxPerCm: number): Promise<string> {
        const user = await this.authService.user
        if (!user) {
            throw new Error("User not found")
        }
        const normalMapAssignment = textureSetRevision.mapAssignments.find((mapAssignment) => mapAssignment.textureType === TextureType.Displacement)
        if (!normalMapAssignment) {
            throw new Error("Normal mapAssignment not found")
        }
        const normalMapDataObjectLegacyId = await this.textureApi.queryDataObjectLegacyId(normalMapAssignment.dataObject.id)
        const displacementGenerationJob = JobNodes.task(displacementGenerationTask, {
            input: JobNodes.struct({
                organizationLegacyId: JobNodes.value(textureSetRevision.textureSet.textureGroup.organization.legacyId),
                normalMapDataObject: JobNodes.value(JobNodes.dataObjectReference(normalMapDataObjectLegacyId)),
                pxPerCm: JobNodes.value(pxPerCm),
            }),
        })
        const displacementMapDataObjectReference = JobNodes.get(displacementGenerationJob, "displacementMapDataObjectReference")
        const displacementValue = JobNodes.get(displacementGenerationJob, "displacementCm")
        const updateOriginalFilenameTask = Utility.DataObject.update(displacementMapDataObjectReference, {
            originalFileName: `TextureMap-${TextureType.Displacement}.exr`,
        })
        const updatedDataObjectReference = JobNodes.get(updateOriginalFilenameTask, "dataObject")
        const thumbnailTask = JobNodes.task(generateThumbnailsDataObjectTask, {
            input: JobNodes.struct({
                dataObject: updatedDataObjectReference,
            }),
        })
        const displacementDataObjectReference = JobNodes.get(JobNodes.get(thumbnailTask, "dataObject"), "dataObjectId")
        const newMapAssignments = JobNodes.list([
            ...textureSetRevision.mapAssignments
                .filter((mapAssignment) => mapAssignment.textureType !== TextureType.Displacement)
                .map((mapAssignment) => {
                    return JobNodes.struct({textureType: JobNodes.value(mapAssignment.textureType), dataObjectId: JobNodes.value(mapAssignment.dataObject.id)})
                }),
            JobNodes.struct({textureType: JobNodes.value(TextureType.Displacement), dataObjectId: displacementDataObjectReference}),
        ])
        const updateTextureSetRevisionJobTask = JobNodes.task(Utility.TextureSetRevision.task, {
            input: JobNodes.struct({
                operation: JobNodes.value("update" as const),
                fields: JobNodes.struct({
                    textureSetRevisionId: JobNodes.value(textureSetRevision.id),
                    updatedById: JobNodes.value(user.id),
                    autoRescaleSize: JobNodes.value(false),
                    displacement: displacementValue,
                    mapAssignments: newMapAssignments,
                }),
            }),
        })
        const jobGraph = JobNodes.jobGraph(updateTextureSetRevisionJobTask, {
            platformVersion: Settings.APP_VERSION,
        })
        const jobId = await this.textureApi.createBakingJob(textureSetRevision, jobGraph)
        return jobId
    }

    private async generateJobGraph(operatorStack: OperatorStack, textureSetRevision: TextureSetRevisionData, pxPerCm: number) {
        const user = await this.authService.user
        if (!user) {
            throw new Error("User not found")
        }
        const organizationId = textureSetRevision.textureSet.textureGroup.organization.id
        const sourceMapDataObjectIdByTextureType = getSourceMapDataObjectIds(textureSetRevision)

        // displacement always mirrors whatever happens to the normal map (see Operator.shouldApplyTo), so it's never
        // baked directly here - it's either regenerated from a processed normal map or reused as-is, further down
        const displacementSourceMapDataObjectId = sourceMapDataObjectIdByTextureType.get(TextureType.Displacement)
        sourceMapDataObjectIdByTextureType.delete(TextureType.Displacement)

        const progressItems: {node: JobNodes.ProgressNode; factor?: number}[] = []
        const PROGRESS_FACTOR_DISPLACEMENT_GENERATION = 0.4
        const PROGRESS_FACTOR_IMAGE_PROCESSING = 0.2
        const PROGRESS_FACTOR_THUMBNAILS = 0.3
        const PROGRESS_FACTOR_MINOR = 0.01

        const filenameForTextureType = (textureType: TextureType) => `TextureMap-${textureType}.exr`

        const processingByTextureType: Map<TextureType, string | JobNodes.TypedDataNode<JobNodes.DataObjectReference>> = new Map()
        {
            const getTrivialGraphDataObjectId = (graph: Nodes.Node): string | undefined => {
                const findTrivialGraphDataObjectId = (node: Nodes.Node): string | undefined => {
                    switch (node.type) {
                        case "levels": {
                            if (typeof node.blackLevel === "number" && typeof node.whiteLevel === "number" && node.blackLevel === 0 && node.whiteLevel === 1) {
                                return findTrivialGraphDataObjectId(node.input)
                            } else {
                                return undefined
                            }
                        }
                        case "convert": {
                            return findTrivialGraphDataObjectId(node.input)
                        }
                        case "decode": {
                            return findTrivialGraphDataObjectId(node.input)
                        }
                        case "encode": {
                            return findTrivialGraphDataObjectId(node.input)
                        }
                        case "setDpi": {
                            return findTrivialGraphDataObjectId(node.input)
                        }
                        case "externalData": {
                            if (typeof node.sourceData.dataObjectId !== "string") {
                                throw new Error("Expected sourceData.dataObjectId to be a string")
                            }
                            return node.sourceData.type === "dataObjectReference" ? node.sourceData.dataObjectId : undefined
                        }
                        default: {
                            return undefined
                        }
                    }
                }
                return findTrivialGraphDataObjectId(graph)
            }

            const getProcessingForTextureType = async (
                textureType: TextureType,
                imageProcessingGraph: Nodes.Output,
            ): Promise<string | JobNodes.TypedDataNode<JobNodes.DataObjectReference>> => {
                const trivialGraphDataObjectId = getTrivialGraphDataObjectId(imageProcessingGraph)
                if (trivialGraphDataObjectId) {
                    // there is no need to processes the image and generate a new one, as the graph is trivial and only contains a decode/encode operation
                    // console.log(`Reusing texture data object for texture type ${textureType} due to trivial image processing graph`)
                    return trivialGraphDataObjectId
                } else {
                    const imgProcTask = await this.imageProcessingService.createImageProcessingTask(
                        imageProcessingGraph,
                        {organizationId},
                        `img-proc-${descriptorByTextureType(textureType).label}.json`,
                    )
                    progressItems.push({node: imgProcTask, factor: PROGRESS_FACTOR_IMAGE_PROCESSING})
                    const dataObjectUpdateTask = Utility.DataObject.update(imgProcTask, {
                        originalFileName: filenameForTextureType(textureType),
                        imageColorSpace: "LINEAR",
                        imageDataType: descriptorByTextureType(textureType).isColorData ? "COLOR" : "NON_COLOR",
                    })
                    progressItems.push({node: dataObjectUpdateTask, factor: PROGRESS_FACTOR_MINOR})
                    return JobNodes.get(dataObjectUpdateTask, "dataObject")
                }
            }

            const textureTypeAndSourceMapDataObjectId = Array.from(sourceMapDataObjectIdByTextureType.entries())
            // we bake sequentially, because some shared data might be uploaded which would otherwise be uploaded multiple times
            const imageProcessingGraphByTextureType = new Map<TextureType, Nodes.Output>()
            for (const [textureType, sourceMapDataObjectId] of textureTypeAndSourceMapDataObjectId) {
                const imageProcessingGraph = await this.bakeTextureEditsGraph(operatorStack, textureType, sourceMapDataObjectId)
                imageProcessingGraphByTextureType.set(textureType, imageProcessingGraph)
            }
            await Promise.all(
                textureTypeAndSourceMapDataObjectId.map(async ([textureType, _]) => {
                    const processing = await getProcessingForTextureType(textureType, imageProcessingGraphByTextureType.get(textureType)!)
                    processingByTextureType.set(textureType, processing)
                }),
            )
        }

        // for the displacement map we either use the existing one when the normal map is untouched, or we regenerate it from the normal map
        let customDisplacementValue: JobNodes.TypedDataNode<number | undefined> = JobNodes.value(undefined)
        const normalMapProcessing = processingByTextureType.get(TextureType.Normal)
        if (normalMapProcessing && typeof normalMapProcessing !== "string") {
            // we process the normal map so let's regenerate the displacement map as well
            const displacementGenerationJob = JobNodes.task(displacementGenerationTask, {
                input: JobNodes.struct({
                    organizationLegacyId: JobNodes.value(textureSetRevision.textureSet.textureGroup.organization.legacyId),
                    normalMapDataObject: normalMapProcessing,
                    pxPerCm: JobNodes.value(pxPerCm),
                }),
            })
            progressItems.push({node: displacementGenerationJob, factor: PROGRESS_FACTOR_DISPLACEMENT_GENERATION})
            const displacementMapDataObjectReference = JobNodes.get(displacementGenerationJob, "displacementMapDataObjectReference")
            customDisplacementValue = JobNodes.get(displacementGenerationJob, "displacementCm")
            const updateOriginalFilenameTask = Utility.DataObject.update(displacementMapDataObjectReference, {
                originalFileName: `TextureMap-${TextureType.Displacement}.exr`,
            })
            progressItems.push({node: updateOriginalFilenameTask, factor: PROGRESS_FACTOR_MINOR})
            const updatedDataObjectReference = JobNodes.get(updateOriginalFilenameTask, "dataObject")
            processingByTextureType.set(TextureType.Displacement, updatedDataObjectReference)
        } else if (displacementSourceMapDataObjectId) {
            // normal map is untouched, so displacement is untouched too - reuse it as-is
            processingByTextureType.set(TextureType.Displacement, displacementSourceMapDataObjectId)
        }

        // thumbnails are generated per new data object *before* the revision is switched over to it, so the revision never references an object without thumbnails
        const newMapAssignments = JobNodes.list(
            Array.from(processingByTextureType.entries()).map(([textureType, newDataObjectReferenceOrId]) => {
                if (typeof newDataObjectReferenceOrId === "string") {
                    return JobNodes.struct({textureType: JobNodes.value(textureType), dataObjectId: JobNodes.value(newDataObjectReferenceOrId)})
                } else {
                    const thumbnailTask = JobNodes.task(generateThumbnailsDataObjectTask, {
                        input: JobNodes.struct({
                            dataObject: newDataObjectReferenceOrId,
                            options: JobNodes.value({generateTiles: TextureEditorSettings.UseVT}),
                        }),
                    })
                    progressItems.push({node: thumbnailTask, factor: PROGRESS_FACTOR_THUMBNAILS})
                    const dataObjectId = JobNodes.get(JobNodes.get(thumbnailTask, "dataObject"), "dataObjectId")
                    return JobNodes.struct({textureType: JobNodes.value(textureType), dataObjectId: dataObjectId})
                }
            }),
        )

        // create the texture-set-revision update task
        const updateTextureSetRevisionJobTask = JobNodes.task(Utility.TextureSetRevision.task, {
            input: JobNodes.struct({
                operation: JobNodes.value("update" as const),
                fields: JobNodes.struct({
                    textureSetRevisionId: JobNodes.value(textureSetRevision.id),
                    updatedById: JobNodes.value(user.id),
                    autoRescaleSize: JobNodes.value(true),
                    displacement: customDisplacementValue,
                    mapAssignments: newMapAssignments,
                }),
            }),
        })
        progressItems.push({node: updateTextureSetRevisionJobTask, factor: PROGRESS_FACTOR_MINOR})

        return JobNodes.jobGraph(updateTextureSetRevisionJobTask, {
            progress: {
                type: "progressGroup",
                items: progressItems,
            },
            platformVersion: Settings.APP_VERSION,
        })
    }

    private async bakeTextureEditsGraph(operatorStack: OperatorStack, textureType: TextureType, dataObjectId: string): Promise<Nodes.Output> {
        const imageOpSystem = operatorStack.imageOpSystem
        const sourceImage = createSourceDataObjectNode(textureType, dataObjectId, "exr", "baking-source")
        const generatedNodeGraph = await operatorStack.generateOperatorNodeGraph("final", textureType, sourceImage)
        const result = await imageOpSystem.evaluate("imgproc", generatedNodeGraph.resultImage)
        let resultNode = result.node
        sourceImage.release(REF_COUNT_DEBUG_ID_INITIAL)
        result.release(REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT)
        const pxPerCm = operatorStack.textureEditorData.textureTypeSpecific?.pxPerCm
        if (pxPerCm) {
            const avgPxPerCm = (pxPerCm.width + pxPerCm.height) / 2
            const dpi = avgPxPerCm * 2.54
            resultNode = Nodes.setDpi(resultNode, dpi)
        }
        return Nodes.encode(resultNode, "image/x-exr")
    }

    private textureApi: TexturesApiService
    private imageProcessingService: ImageProcessingService
    private authService: AuthService
}
